/**
 * Quick Tour Component
 * Interactive walkthrough for new users to learn DAW features
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Play, 
  Music, 
  Sliders, 
  Keyboard,
  Sparkles,
  CheckCircle,
  RotateCcw
} from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to String Sauce!',
    description: 'Let\'s take a quick tour of your new mobile music studio. This will only take a minute!',
    icon: Sparkles,
    color: 'from-orange-500 to-amber-500',
    target: null, // No highlight for welcome
    position: 'center',
  },
  {
    id: 'transport',
    title: 'Transport Controls',
    description: 'Play, pause, stop, and record your music. The big green button starts playback. Press Space for quick play/pause!',
    icon: Play,
    color: 'from-emerald-500 to-green-500',
    target: '[data-testid="transport-play"]',
    position: 'bottom',
    shortcut: 'Space',
  },
  {
    id: 'tracks',
    title: 'Multi-Track Recording',
    description: 'Add up to 8 tracks (or unlimited with Studio!). Each track can have audio, synth sounds, or drums. Click "Audio Track" to add more.',
    icon: Music,
    color: 'from-cyan-500 to-blue-500',
    target: '[data-testid="track-controls-0"]',
    position: 'right',
  },
  {
    id: 'mixer',
    title: 'Pro Mixing Controls',
    description: 'Control volume, panning, mute (M) and solo (S) for each track. Perfect your mix with precision!',
    icon: Sliders,
    color: 'from-purple-500 to-pink-500',
    target: '[data-testid="view-tab-mix"]',
    position: 'bottom',
    shortcut: 'Ctrl+2',
  },
  {
    id: 'effects',
    title: 'Guitar Effects Suite',
    description: 'Built-in amp models and pedal effects. Perfect for guitarists! Access via the "Guitar Effects" button on each track.',
    icon: Sparkles,
    color: 'from-amber-500 to-orange-500',
    target: '[data-testid="toggle-effects"]',
    position: 'bottom',
  },
  {
    id: 'shortcuts',
    title: 'Keyboard Shortcuts',
    description: 'Press ? anytime to see all keyboard shortcuts. Pro tip: Space = Play, R = Record, L = Loop!',
    icon: Keyboard,
    color: 'from-indigo-500 to-violet-500',
    target: null,
    position: 'center',
    shortcut: '?',
  },
  {
    id: 'complete',
    title: 'You\'re Ready to Rock!',
    description: 'Start creating music! Import audio, record from your mic, or use the built-in instruments. Have fun!',
    icon: CheckCircle,
    color: 'from-emerald-500 to-teal-500',
    target: null,
    position: 'center',
  },
];

export const QuickTour = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = TOUR_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  // Find and highlight target element
  useEffect(() => {
    if (!isOpen || !step.target) {
      setTargetRect(null);
      return;
    }

    const findTarget = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      } else {
        setTargetRect(null);
      }
    };

    findTarget();
    // Re-find on scroll/resize
    const handleUpdate = () => findTarget();
    window.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, step.target, currentStep]);

  const nextStep = useCallback(() => {
    if (isLastStep) {
      // Mark tour as complete
      localStorage.setItem('string-sauce-tour-complete', 'true');
      onComplete?.();
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [isLastStep, onClose, onComplete]);

  const prevStep = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isFirstStep]);

  const skipTour = useCallback(() => {
    localStorage.setItem('string-sauce-tour-complete', 'true');
    onClose();
  }, [onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        skipTour();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, nextStep, prevStep, skipTour]);

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (!targetRect || step.position === 'center') {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const padding = 16;
    const tooltipWidth = 380;
    const tooltipHeight = 220;

    let top, left;

    switch (step.position) {
      case 'bottom':
        top = targetRect.top + targetRect.height + padding;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'top':
        top = targetRect.top - tooltipHeight - padding;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - tooltipWidth - padding;
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left + targetRect.width + padding;
        break;
      default:
        top = targetRect.top + targetRect.height + padding;
        left = targetRect.left;
    }

    // Clamp to viewport
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));

    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
    };
  };

  const IconComponent = step.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with cutout for target */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100]"
            style={{
              background: targetRect
                ? `radial-gradient(circle at ${targetRect.left + targetRect.width / 2}px ${targetRect.top + targetRect.height / 2}px, transparent ${Math.max(targetRect.width, targetRect.height)}px, rgba(0,0,0,0.85) ${Math.max(targetRect.width, targetRect.height) + 50}px)`
                : 'rgba(0,0,0,0.85)',
            }}
          />

          {/* Highlight ring around target */}
          {targetRect && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="fixed z-[101] pointer-events-none"
              style={{
                top: targetRect.top - 4,
                left: targetRect.left - 4,
                width: targetRect.width + 8,
                height: targetRect.height + 8,
                border: '2px solid rgba(249, 115, 22, 0.8)',
                borderRadius: '8px',
                boxShadow: '0 0 20px rgba(249, 115, 22, 0.5), 0 0 40px rgba(249, 115, 22, 0.3)',
              }}
            />
          )}

          {/* Tooltip Card */}
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="z-[102] w-[380px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden"
            style={getTooltipStyle()}
            data-testid="quick-tour-tooltip"
          >
            {/* Header with gradient */}
            <div className={`px-5 py-4 bg-gradient-to-r ${step.color}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{step.title}</h3>
                    <p className="text-xs text-white/70">
                      Step {currentStep + 1} of {TOUR_STEPS.length}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={skipTour}
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 py-4">
              <p className="text-sm text-zinc-300 leading-relaxed">
                {step.description}
              </p>
              
              {step.shortcut && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-zinc-500">Shortcut:</span>
                  <kbd className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs font-mono text-orange-400">
                    {step.shortcut}
                  </kbd>
                </div>
              )}
            </div>

            {/* Progress dots */}
            <div className="px-5 pb-2">
              <div className="flex justify-center gap-1.5">
                {TOUR_STEPS.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentStep
                        ? 'bg-orange-500'
                        : idx < currentStep
                        ? 'bg-orange-500/50'
                        : 'bg-zinc-700'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="px-5 py-3 bg-zinc-800/50 border-t border-zinc-700 flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={prevStep}
                disabled={isFirstStep}
                className="text-zinc-400 hover:text-white disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={skipTour}
                className="text-zinc-500 hover:text-zinc-300 text-xs"
              >
                Skip Tour
              </Button>

              <Button
                size="sm"
                onClick={nextStep}
                className={`bg-gradient-to-r ${step.color} text-white font-medium`}
              >
                {isLastStep ? 'Start Creating!' : 'Next'}
                {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Hook to manage tour state
export const useQuickTour = () => {
  const [showTour, setShowTour] = useState(false);
  const [tourComplete, setTourComplete] = useState(() => {
    return localStorage.getItem('string-sauce-tour-complete') === 'true';
  });

  const startTour = useCallback(() => {
    setShowTour(true);
  }, []);

  const closeTour = useCallback(() => {
    // Mark as seen even if skipped
    localStorage.setItem('string-sauce-tour-complete', 'true');
    setShowTour(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem('string-sauce-tour-complete', 'true');
    setTourComplete(true);
    setShowTour(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem('string-sauce-tour-complete');
    setTourComplete(false);
  }, []);

  // Auto-show tour for new users (only once)
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('string-sauce-tour-complete');
    const isFirstVisit = !localStorage.getItem('string-sauce-visited');
    
    if (!hasSeenTour && isFirstVisit) {
      // Delay tour start to let DAW fully load
      const timer = setTimeout(() => {
        setShowTour(true);
        localStorage.setItem('string-sauce-visited', 'true');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  return {
    showTour,
    tourComplete,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
};

// Button to restart tour - requires onStartTour callback from parent
export const RestartTourButton = ({ className = '', onStartTour }) => {
  const handleClick = () => {
    // Clear the tour complete flag
    localStorage.removeItem('string-sauce-tour-complete');
    // Trigger tour start via parent callback
    if (onStartTour) {
      onStartTour();
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className={`text-zinc-400 hover:text-white ${className}`}
      title="Take the tour again"
      data-testid="restart-tour-btn"
    >
      <RotateCcw className="w-4 h-4 mr-1" />
      Tour
    </Button>
  );
};

export default QuickTour;
